import React, { useMemo } from "react";

import AddIcon from "@mui/icons-material/Add";
import HubIcon from "@mui/icons-material/Hub";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  LinearProgress,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { Navigate } from "react-router";

import { orgSessionManager } from "src/domains/auth/services/orgSessionManager";
import DashboardSkeleton from "src/domains/dashboard/components/DashboardSkeleton";
import { useDashboardData } from "src/domains/dashboard/hooks/useDashboardData";
import type {
  ActivityItem,
  HealthMetric,
  KpiTile,
  RecentDeploymentRow,
  RecentProjectRow,
} from "src/domains/dashboard/types/DashboardTypes";
import { ORGANIZATIONS, PROJECTS, STACKS } from "src/platform/constants/routes";
import { getOrgAliasFromToken } from "src/platform/utils/jwt";
import ErrorCard from "src/shell/components/ErrorCard";
import {
  getCardStyles,
  getContainerStyles,
  sectionLabelSx,
} from "src/shell/styles/themeUtils";

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Statuses that count as "operational" (running or actively deploying). */
const OPERATIONAL_STATUSES = new Set([
  "running",
  "active",
  "deploying",
  "building",
  "provisioning",
]);

type DotColor = "success" | "error" | "warning" | "primary";

/** Map a status string to a semantic dot color. */
function statusColor(status: string): DotColor {
  const s = (status ?? "").toLowerCase();
  if (["running", "active", "healthy", "completed"].includes(s))
    return "success";
  if (["failed", "error", "unhealthy"].includes(s)) return "error";
  if (["degraded", "pending", "warning", "stopped", "draft"].includes(s))
    return "warning";
  if (
    ["building", "deploying", "provisioning", "queued", "in_progress"].includes(
      s,
    )
  )
    return "primary";
  return "primary";
}

/** Map a deployment-event severity to a dot color. */
function severityColor(severity: string): DotColor {
  const s = (severity ?? "").toLowerCase();
  if (s === "error") return "error";
  if (s === "warning") return "warning";
  if (s === "info") return "primary";
  return "success";
}

/** Map a notification type to a dot color. */
function notificationColor(type: string): DotColor {
  const t = (type ?? "").toLowerCase();
  if (t.includes("failed") || t.includes("error")) return "error";
  if (t.includes("retry") || t.includes("warning")) return "warning";
  if (
    t.includes("provisioned") ||
    t.includes("running") ||
    t.includes("success")
  )
    return "success";
  return "primary";
}

/** "12m ago" / "2h ago" / "1d ago" style relative time. */
function relativeTime(iso: string | null): string {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) return "just now";
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/** Format a deployment event's step as a target environment label. */
function stepToTarget(step: string): string {
  const map: Record<string, string> = {
    build: "build",
    deploy: "deploy",
    vcs: "vcs",
  };
  return map[step] ?? step;
}

// ─── Dashboard content (rendered when org is selected) ───────────────────────

interface DashboardContentProps {
  orgAlias: string;
}

const DashboardContent: React.FC<DashboardContentProps> = ({ orgAlias }) => {
  const theme = useTheme();
  const {
    orgSummary,
    projects,
    apps,
    deploymentEvents,
    notifications,
    billing,
    billingLoading,
    loading,
    error,
    retry,
  } = useDashboardData(orgAlias);

  // Build a map from app alias → project alias for stack detail links.
  const appToProject = useMemo(() => {
    const map = new Map<string, string>();
    for (const app of apps) {
      const project = projects.find((p) => p.id === app.projectId);
      if (project) map.set(app.alias, project.alias);
    }
    return map;
  }, [apps, projects]);

  // ── KPI tiles ──────────────────────────────────────────────────────────────

  const kpis: KpiTile[] = useMemo(() => {
    const projectCount = orgSummary?.projectCount ?? projects.length;
    const stackCount = orgSummary?.systemCount ?? apps.length;
    const runningCount = orgSummary?.runningCount ?? 0;
    const stoppedCount = stackCount - runningCount;

    const resourceCount = projects.reduce(
      (sum, p) => sum + (p.resourceCount ?? 0),
      0,
    );

    const totalUsd = billing?.totalUsd;
    const costLabel =
      billing == null
        ? billingLoading
          ? "Loading…"
          : "—"
        : billing.dataQuality === "cold"
          ? "Warming up…"
          : billing.dataQuality === "empty"
            ? "No data yet"
            : `$${totalUsd}`;

    const planLabel = billing?.plan ?? (billingLoading ? "…" : "—");

    return [
      {
        value: String(projectCount),
        label: "Projects",
        trend: `${stackCount} stack${stackCount !== 1 ? "s" : ""}`,
      },
      {
        value: String(stackCount),
        label: "Stacks",
        trend: `${runningCount} running, ${stoppedCount} stopped`,
      },
      {
        value: String(resourceCount),
        label: "Resources",
        trend: `${resourceCount} provisioned`,
      },
      { value: costLabel, label: "MTD cost", trend: planLabel },
    ];
  }, [orgSummary, projects, apps, billing, billingLoading]);

  // ── Health metrics ─────────────────────────────────────────────────────────

  const healthMetrics: HealthMetric[] = useMemo(() => {
    const totalStacks = orgSummary?.systemCount ?? apps.length;
    const operationalStacks = apps.filter((a) =>
      OPERATIONAL_STATUSES.has((a.status ?? "").toLowerCase()),
    ).length;
    const failedStacks = orgSummary?.failedCount ?? 0;
    const healthyStacks = operationalStacks;
    const stacksPercent =
      totalStacks > 0 ? Math.round((healthyStacks / totalStacks) * 100) : 0;

    const resourceTotal = projects.reduce(
      (sum, p) => sum + (p.resourceCount ?? 0),
      0,
    );
    const resourcesPercent = resourceTotal > 0 ? 100 : 0;

    const pipelinesTotal = apps.length;
    const pipelinesHealthy = apps.filter(
      (a) => !["failed", "error"].includes((a.status ?? "").toLowerCase()),
    ).length;
    const pipelinesPercent =
      pipelinesTotal > 0
        ? Math.round((pipelinesHealthy / pipelinesTotal) * 100)
        : 0;

    const stacksColor =
      failedStacks > 0
        ? "error"
        : stacksPercent >= 75
          ? "success"
          : stacksPercent >= 50
            ? "warning"
            : "error";

    const pipelinesColor =
      pipelinesPercent >= 75
        ? "success"
        : pipelinesPercent >= 50
          ? "warning"
          : "error";

    return [
      {
        label: "Stacks",
        ratio: `${healthyStacks}/${totalStacks}`,
        percent: stacksPercent,
        color: stacksColor,
      },
      {
        label: "Resources",
        ratio: `${resourceTotal}/${resourceTotal}`,
        percent: resourcesPercent,
        color: "success",
      },
      {
        label: "Pipelines",
        ratio: `${pipelinesHealthy}/${pipelinesTotal}`,
        percent: pipelinesPercent,
        color: pipelinesColor,
      },
    ];
  }, [orgSummary, apps, projects]);

  // ── Recent deployments (from real deployment events) ───────────────────────

  const recentDeployments: RecentDeploymentRow[] = useMemo(() => {
    return deploymentEvents.slice(0, 4).map((ev) => {
      const projectAlias = appToProject.get(ev.systemAlias);
      const href = projectAlias
        ? STACKS.DETAILS.getPath(orgAlias, projectAlias, ev.systemAlias)
        : ORGANIZATIONS.DETAILS.getPath(orgAlias);

      return {
        title: `${ev.systemAlias} → ${stepToTarget(ev.step)}`,
        meta: `${ev.status} · ${relativeTime(ev.createdAt)}`,
        status: statusColor(ev.status),
        href,
      };
    });
  }, [deploymentEvents, appToProject, orgAlias]);

  // ── Recent projects ────────────────────────────────────────────────────────

  const recentProjects: RecentProjectRow[] = useMemo(() => {
    return projects.slice(0, 3).map((p) => ({
      name: p.name,
      alias: p.alias,
      meta: `${p.appCount} stack${p.appCount !== 1 ? "s" : ""} · ${p.resourceCount} resource${p.resourceCount !== 1 ? "s" : ""}`,
      href: PROJECTS.DETAILS.getPath(orgAlias, p.alias),
    }));
  }, [projects, orgAlias]);

  // ── Activity feed (from deployment events + notifications) ─────────────────

  const activityFeed: ActivityItem[] = useMemo(() => {
    const items: ActivityItem[] = [];

    // From deployment events
    for (const ev of deploymentEvents.slice(0, 4)) {
      items.push({
        text: (
          <>
            <strong>{ev.systemAlias}</strong> — {ev.message}
          </>
        ),
        time: relativeTime(ev.createdAt),
        status: severityColor(ev.severity),
      });
    }

    // From notifications
    for (const n of notifications.slice(0, 4)) {
      items.push({
        text: (
          <>
            <strong>{n.title}</strong> — {n.body}
          </>
        ),
        time: relativeTime(n.createdAt),
        status: notificationColor(n.type),
      });
    }

    // Merge and sort by createdAt descending, take top 4
    return items.slice(0, 4);
  }, [deploymentEvents, notifications]);

  // ── Health chip ────────────────────────────────────────────────────────────

  const isOperational = (orgSummary?.failedCount ?? 0) === 0;
  const healthChipLabel = isOperational ? "Operational" : "Degraded";
  const healthChipColor = isOperational ? "success" : "error";

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) return <DashboardSkeleton />;

  if (error) {
    return (
      <ErrorCard
        error={error}
        title="Unable to load dashboard"
        onRetry={retry}
      />
    );
  }

  const cardSx = getCardStyles(theme);

  return (
    <Container sx={getContainerStyles(theme)}>
      {/* Page header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{ mb: 3 }}
      >
        <Stack spacing={0.5}>
          <Typography variant="h4" fontWeight={700}>
            Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Overview of your organization and recent activity.
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            href={PROJECTS.CREATE.getPath(orgAlias)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Create project
          </Button>
          <Button
            variant="outlined"
            startIcon={<HubIcon />}
            href={ORGANIZATIONS.DETAILS.getPath(orgAlias)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Adopt stack
          </Button>
        </Stack>
      </Stack>

      {/* KPI grid */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {kpis.map((kpi) => (
          <Grid item xs={12} sm={6} md={3} key={kpi.label}>
            <Stack sx={cardSx} spacing={0.5}>
              <Typography variant="h5" fontWeight={700}>
                {kpi.value}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {kpi.label}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color:
                    kpi.trend.includes("stopped") ||
                    kpi.trend.includes("failed")
                      ? theme.palette.warning.main
                      : theme.palette.success.main,
                  mt: 0.5,
                }}
              >
                {kpi.trend}
              </Typography>
            </Stack>
          </Grid>
        ))}
      </Grid>

      {/* 2-column grid */}
      <Grid container spacing={2}>
        {/* Left column */}
        <Grid item xs={12} md={6}>
          <Stack spacing={2}>
            {/* Health summary */}
            <Stack sx={cardSx} spacing={1.5}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography sx={sectionLabelSx}>Health summary</Typography>
                <Chip
                  label={healthChipLabel}
                  size="small"
                  color={healthChipColor}
                  variant="filled"
                  sx={{ fontWeight: 500 }}
                />
              </Stack>
              <Grid container spacing={1.5}>
                {healthMetrics.map((m) => (
                  <Grid item xs={4} key={m.label}>
                    <Stack
                      spacing={0.5}
                      sx={{
                        p: 1.5,
                        border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {m.label}
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {m.ratio}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={m.percent}
                        color={m.color}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          mt: 0.5,
                          bgcolor: alpha(theme.palette.divider, 0.08),
                        }}
                      />
                    </Stack>
                  </Grid>
                ))}
              </Grid>
            </Stack>

            {/* Recent deployments */}
            <Stack sx={cardSx} spacing={1}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography sx={sectionLabelSx}>Recent deployments</Typography>
                <Typography
                  variant="caption"
                  color="primary.main"
                  component="a"
                  href={ORGANIZATIONS.DETAILS.getPath(orgAlias)}
                  sx={{ textDecoration: "none", fontWeight: 600 }}
                >
                  View all
                </Typography>
              </Stack>
              {recentDeployments.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2 }}
                >
                  No deployments yet.
                </Typography>
              ) : (
                recentDeployments.map((dep) => (
                  <Stack
                    key={dep.title + dep.meta}
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{
                      p: 1.5,
                      border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                      borderRadius: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: theme.palette[dep.status].main,
                        flexShrink: 0,
                      }}
                    />
                    <Stack spacing={0.25} sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {dep.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {dep.meta}
                      </Typography>
                    </Stack>
                    <Button
                      size="small"
                      variant="outlined"
                      href={dep.href}
                      endIcon={<OpenInNewIcon fontSize="small" />}
                      sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                      Open
                    </Button>
                  </Stack>
                ))
              )}
            </Stack>
          </Stack>
        </Grid>

        {/* Right column */}
        <Grid item xs={12} md={6}>
          <Stack spacing={2}>
            {/* Quick actions */}
            <Stack sx={cardSx} spacing={1}>
              <Typography sx={sectionLabelSx}>Quick actions</Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <QuickActionCard
                    icon={<AddIcon />}
                    name="Create project"
                    meta="Add a new team workspace"
                    href={PROJECTS.CREATE.getPath(orgAlias)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <QuickActionCard
                    icon={<HubIcon />}
                    name="Adopt stack"
                    meta="Connect an existing repo"
                    href={ORGANIZATIONS.DETAILS.getPath(orgAlias)}
                  />
                </Grid>
              </Grid>
            </Stack>

            {/* Recent projects */}
            <Stack sx={cardSx} spacing={1}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography sx={sectionLabelSx}>Recent projects</Typography>
                <Typography
                  variant="caption"
                  color="primary.main"
                  component="a"
                  href={PROJECTS.LIST.getPath(orgAlias)}
                  sx={{ textDecoration: "none", fontWeight: 600 }}
                >
                  View all
                </Typography>
              </Stack>
              {recentProjects.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2 }}
                >
                  No projects yet.
                </Typography>
              ) : (
                recentProjects.map((p) => (
                  <Stack
                    key={p.alias}
                    component="a"
                    href={p.href}
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{
                      p: 1.75,
                      border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                      borderRadius: 1,
                      textDecoration: "none",
                      color: "inherit",
                      transition:
                        "border-color 150ms ease, box-shadow 150ms ease",
                      "&:hover": {
                        borderColor: alpha(theme.palette.divider, 0.12),
                        boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.04)}`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        color: theme.palette.primary.main,
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {p.name.charAt(0).toUpperCase()}
                    </Box>
                    <Stack spacing={0.25} sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {p.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {p.meta}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.disabled">
                      →
                    </Typography>
                  </Stack>
                ))
              )}
            </Stack>

            {/* Activity feed */}
            <Stack sx={cardSx} spacing={0}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography sx={sectionLabelSx}>Activity feed</Typography>
                <Typography
                  variant="caption"
                  color="primary.main"
                  component="a"
                  href={ORGANIZATIONS.DETAILS.getPath(orgAlias)}
                  sx={{ textDecoration: "none", fontWeight: 600 }}
                >
                  View all
                </Typography>
              </Stack>
              {activityFeed.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2 }}
                >
                  No recent activity.
                </Typography>
              ) : (
                activityFeed.map((item, i) => (
                  <Stack
                    key={i}
                    direction="row"
                    spacing={1.5}
                    alignItems="flex-start"
                    sx={{
                      py: 1.25,
                      borderBottom:
                        i < activityFeed.length - 1
                          ? `1px solid ${alpha(theme.palette.divider, 0.06)}`
                          : "none",
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: theme.palette[item.status].main,
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Stack spacing={0.25}>
                      <Typography variant="body2">{item.text}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.time}
                      </Typography>
                    </Stack>
                  </Stack>
                ))
              )}
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </Container>
  );
};

interface QuickActionCardProps {
  icon: React.ReactNode;
  name: string;
  meta: string;
  href: string;
}

const QuickActionCard: React.FC<QuickActionCardProps> = ({
  icon,
  name,
  meta,
  href,
}) => {
  const theme = useTheme();
  return (
    <Stack
      component="a"
      href={href}
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{
        p: 1.75,
        height: "100%",
        border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
        borderRadius: 1,
        textDecoration: "none",
        color: "inherit",
        transition: "border-color 150ms ease, box-shadow 150ms ease",
        "&:hover": {
          borderColor: alpha(theme.palette.divider, 0.12),
          boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.04)}`,
        },
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          color: theme.palette.primary.main,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Stack spacing={0.25} sx={{ flex: 1 }}>
        <Typography variant="body2" fontWeight={600}>
          {name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {meta}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.disabled">
        →
      </Typography>
    </Stack>
  );
};

// ─── Dashboard (smart redirect + content) ────────────────────────────────────

/**
 * Dashboard — org-scoped overview.
 *
 * Reads the active org from the JWT (signed by the server). If the JWT is a
 * step-2 org-scoped token, render the dashboard for that org. Otherwise
 * redirect to org selection so the user can establish a step-2 session.
 */
const Dashboard: React.FC = () => {
  const token = orgSessionManager.getToken();
  const orgAlias = getOrgAliasFromToken(token);

  if (!orgAlias) {
    return <Navigate to={ORGANIZATIONS.LIST.path} replace />;
  }

  return <DashboardContent orgAlias={orgAlias} />;
};

export default Dashboard;
